import {
  computeBranchRelation,
  computeElementRelation,
} from "../zodiac/chinese/chineseZodiacInteractions.js";
import { buildCoupleMatchNarratives, resolveCoupleMatchLine } from "./coupleMatchNarrative.js";
import {
  DEFAULT_BIRTH_HOUR,
  buildBaziChart,
  elementCountsFromPillars,
  parsePartnerBirthDate,
  normalizeBirthHour,
  pillarAnimal,
  serializeDaYun,
  serializePillars,
} from "./coupleMatchBazi.js";

const PILLAR_KEYS = ["year", "month", "day", "hour"];
const RELATION_SCORE = {
  liu_he: 18,
  san_he: 12,
  same: 6,
  neutral: 0,
  liu_chong: -22,
};
const INTERACTION_PENALTY = {
  CLASH: -12,
  HARM: -8,
  PUNISHMENT: -10,
  DESTRUCTION: -6,
  COMBINATION_2: 10,
  TRINE: 12,
  DIRECTIONAL: 8,
};

function digestSeed(parts) {
  const input = parts.join(":");
  if (typeof globalThis !== "undefined" && globalThis.crypto?.subtle) {
    // Sync fallback for tests/SSR — use simple hash
  }
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0;
  }
  return hash;
}

export function coupleMatchSeed(partnerA, partnerB) {
  const a = partnerA?.birthDate || "";
  const b = partnerB?.birthDate || "";
  const ah = normalizeBirthHour(partnerA?.birthHour);
  const bh = normalizeBirthHour(partnerB?.birthHour);
  return digestSeed(["couple_match", a, String(ah), b, String(bh)]);
}

function relationScore(relation) {
  return RELATION_SCORE[relation] ?? 0;
}

function clampScore(value) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function scoreBand(score) {
  if (score >= 75) return "excellent";
  if (score >= 55) return "good";
  return "caution";
}

function elementRelationScore(relation) {
  switch (relation) {
    case "same":
      return 8;
    case "generates":
    case "generated_by":
      return 6;
    case "overcomes":
    case "overcome_by":
      return -10;
    default:
      return 0;
  }
}

function buildPillarMatrix(pillarsA, pillarsB) {
  return pillarsA.flatMap((a) =>
    pillarsB.map((b) => {
      const branchRelation = computeBranchRelation(a.animal, b.animal);
      const elementRelation = computeElementRelation(a.element, b.element);
      const score = relationScore(branchRelation) + elementRelationScore(elementRelation);
      return {
        aKey: a.key,
        bKey: b.key,
        branchRelation,
        elementRelation,
        score: clampScore(50 + score),
        severity: score >= 10 ? "harmony" : score <= -10 ? "clash" : "neutral",
      };
    })
  );
}

function matrixAverage(matrix) {
  if (!matrix.length) return 50;
  return matrix.reduce((sum, cell) => sum + cell.score, 0) / matrix.length;
}

function interactionAdjustment(interactionsA, interactionsB) {
  const all = [...(interactionsA || []), ...(interactionsB || [])];
  return all.reduce((sum, item) => sum + (INTERACTION_PENALTY[item.type] || 0), 0);
}

function elementComplementScore(countsA, countsB) {
  const elements = ["Wood", "Fire", "Earth", "Metal", "Water"];
  let balance = 0;
  elements.forEach((el) => {
    const total = (countsA[el] || 0) + (countsB[el] || 0);
    if (total >= 1.5 && total <= 4) balance += 4;
    if (total > 5) balance -= 3;
  });
  return balance;
}

function buildDimensions(baseScore, matrix, yearRelation, dayRelation) {
  const dims = [
    { key: "love", weight: 1.1, bias: relationScore(dayRelation) },
    { key: "harmony", weight: 1.0, bias: matrixAverage(matrix) - 50 },
    { key: "wealth", weight: 0.9, bias: relationScore(yearRelation) / 2 },
    { key: "family", weight: 0.95, bias: 0 },
    { key: "longevity", weight: 0.85, bias: 0 },
  ];
  return dims.map(({ key, weight, bias }) => {
    const score = clampScore(baseScore * 0.55 + 22 + bias * weight);
    return { key, score, band: scoreBand(score) };
  });
}

function activeDaYunCycle(cycles, year) {
  return (cycles || []).find((cycle) => year >= cycle.startYear && year <= cycle.endYear) || null;
}

function yearBranchCharFor(year) {
  const stems = "甲乙丙丁戊己庚辛壬癸";
  const branches = "子丑寅卯辰巳午未申酉戌亥";
  const stem = stems[(year - 4) % 10];
  const branch = branches[(year - 4) % 12];
  return { stem, branch, animal: pillarAnimal(branch) };
}

function buildJointTimeline(pillarsA, pillarsB, daYunA, daYunB, startYear, span = 25) {
  const years = [];
  for (let i = 0; i < span; i += 1) {
    const year = startYear + i;
    const annual = yearBranchCharFor(year);
    const dayA = pillarsA.find((p) => p.key === "day")?.animal;
    const dayB = pillarsB.find((p) => p.key === "day")?.animal;
    const relA = dayA ? computeBranchRelation(dayA, annual.animal) : "neutral";
    const relB = dayB ? computeBranchRelation(dayB, annual.animal) : "neutral";
    const cycleA = activeDaYunCycle(daYunA, year);
    const cycleB = activeDaYunCycle(daYunB, year);
    let toneScore = relationScore(relA) + relationScore(relB);
    if (cycleA) toneScore += 4;
    if (cycleB) toneScore += 4;
    if (cycleA && cycleB) {
      toneScore += relationScore(computeBranchRelation(cycleA.animal, cycleB.animal)) / 2;
    }
    const tone = toneScore >= 14 ? "auspicious" : toneScore <= -8 ? "caution" : "neutral";
    years.push({
      year,
      tone,
      annualAnimal: annual.animal,
      ganZhi: `${annual.stem}${annual.branch}`,
      factors: {
        relA,
        relB,
        cycleA: cycleA ? { animal: cycleA.animal, ganZhi: cycleA.ganZhi } : null,
        cycleB: cycleB ? { animal: cycleB.animal, ganZhi: cycleB.ganZhi } : null,
        toneScore,
      },
    });
  }
  return years;
}

function lifeTogetherTone(overallScore) {
  if (overallScore >= 75) return "supportive";
  if (overallScore >= 55) return "balanced";
  return "challenging";
}

export function computeCoupleMatch(partnerA, partnerB, { t, startYear, locale } = {}) {
  const parsedA = parsePartnerBirthDate(partnerA?.birthDate);
  const parsedB = parsePartnerBirthDate(partnerB?.birthDate);
  if (!parsedA || !parsedB) {
    throw new Error("invalid_birth_date");
  }

  const hourA = normalizeBirthHour(partnerA?.birthHour);
  const hourB = normalizeBirthHour(partnerB?.birthHour);
  const chartA = buildBaziChart({ ...parsedA, hour: hourA, gender: "male" });
  const chartB = buildBaziChart({ ...parsedB, hour: hourB, gender: "female" });

  const pillarsA = serializePillars(chartA);
  const pillarsB = serializePillars(chartB);
  const daYunA = serializeDaYun(chartA);
  const daYunB = serializeDaYun(chartB);

  const yearA = pillarsA.find((p) => p.key === "year");
  const yearB = pillarsB.find((p) => p.key === "year");
  const dayA = pillarsA.find((p) => p.key === "day");
  const dayB = pillarsB.find((p) => p.key === "day");

  const yearRelation = computeBranchRelation(yearA.animal, yearB.animal);
  const dayRelation = computeBranchRelation(dayA.animal, dayB.animal);
  const dayElementRelation = computeElementRelation(dayA.element, dayB.element);

  const matrix = buildPillarMatrix(pillarsA, pillarsB);
  const matrixAvg = matrixAverage(matrix);
  const interactionAdj = interactionAdjustment(chartA.interactions, chartB.interactions);
  const elementAdj = elementComplementScore(
    elementCountsFromPillars(pillarsA),
    elementCountsFromPillars(pillarsB)
  );

  const rawOverall =
    matrixAvg * 0.45 +
    28 +
    relationScore(yearRelation) * 0.8 +
    relationScore(dayRelation) * 1.2 +
    elementRelationScore(dayElementRelation) * 0.8 +
    interactionAdj * 0.6 +
    elementAdj;

  const overallScore = clampScore(rawOverall);
  const dimensions = buildDimensions(overallScore, matrix, yearRelation, dayRelation);
  const jointTimeline = buildJointTimeline(
    pillarsA,
    pillarsB,
    daYunA,
    daYunB,
    startYear || new Date().getFullYear()
  );

  const seed = coupleMatchSeed(
    { birthDate: partnerA.birthDate, birthHour: hourA },
    { birthDate: partnerB.birthDate, birthHour: hourB }
  );
  const tone = lifeTogetherTone(overallScore);
  const variant = seed % 8;

  const baseResult = {
    engine: "openfate/bazi-engine",
    overall_score: overallScore,
    overall_band: scoreBand(overallScore),
    dimensions,
    pillar_matrix: matrix,
    partners: {
      a: {
        label: partnerA?.name || "",
        birthDate: partnerA.birthDate,
        birthHour: hourA,
        usedDefaultHour: partnerA?.birthHour === "" || partnerA?.birthHour == null,
        pillars: pillarsA,
        elementProfile: elementCountsFromPillars(pillarsA),
        daYun: daYunA,
        dayMaster: chartA.dayMaster?.char,
        zodiac: chartA.calendar?.zodiac,
      },
      b: {
        label: partnerB?.name || "",
        birthDate: partnerB.birthDate,
        birthHour: hourB,
        usedDefaultHour: partnerB?.birthHour === "" || partnerB?.birthHour == null,
        pillars: pillarsB,
        elementProfile: elementCountsFromPillars(pillarsB),
        daYun: daYunB,
        dayMaster: chartB.dayMaster?.char,
        zodiac: chartB.calendar?.zodiac,
      },
    },
    animal_summary: {
      yearRelation,
      dayRelation,
      dayElementRelation,
    },
    joint_timeline: jointTimeline,
    meta: {
      default_hour: DEFAULT_BIRTH_HOUR,
      seed,
    },
  };

  const narratives =
    typeof t === "function"
      ? buildCoupleMatchNarratives(baseResult, t, { currentYear: startYear || new Date().getFullYear(), locale })
      : null;

  const lifeTogether = narratives?.lifeTogether || {
    tone,
    variant,
    key: `couple_match_life_together_${tone}_${variant}`,
    text:
      typeof t === "function"
        ? resolveCoupleMatchLine(t, `couple_match_life_together_${tone}_${variant}`)
        : "",
  };

  return {
    ...baseResult,
    life_together: lifeTogether,
    narratives,
  };
}

export function validateCoupleMatchInput(partnerA, partnerB) {
  if (!partnerA?.birthDate || !partnerB?.birthDate) return "required_dob";
  if (!parsePartnerBirthDate(partnerA.birthDate) || !parsePartnerBirthDate(partnerB.birthDate)) {
    return "invalid_dob";
  }
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const aDate = new Date(`${partnerA.birthDate}T00:00:00`);
  const bDate = new Date(`${partnerB.birthDate}T00:00:00`);
  if (aDate > todayStart || bDate > todayStart) return "future_dob";
  return null;
}
